import React, { useMemo, useState } from "react";
import { BarChart3, Calendar, Users, Package, TrendingUp, Clock, Download } from "lucide-react";
import Wrapper from "../../../components/Wrapper";

export default function LabReportsPage() {
  const [reportType, setReportType] = useState("daily");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const workers = [
    { id: "w1", name: "Worker A" },
    { id: "w2", name: "Worker B" },
    { id: "w3", name: "Worker C" },
  ];
  const reagents = [
    { id: "r1", name: "NPK Reagent" },
    { id: "r2", name: "Extractant" },
    { id: "r3", name: "Micronutrient Blend" },
  ];

  // Mock logs so daily/weekly/bi-monthly views produce distinct output.
  const workLogs = [
    { date: "2026-04-09", workerId: "w1", reagentId: "r1", completed: 120, target: 130, hours: 7.5 },
    { date: "2026-04-09", workerId: "w2", reagentId: "r2", completed: 90, target: 100, hours: 7.0 },
    { date: "2026-04-09", workerId: "w3", reagentId: "r3", completed: 70, target: 90, hours: 6.5 },
    { date: "2026-04-08", workerId: "w1", reagentId: "r1", completed: 110, target: 120, hours: 7.0 },
    { date: "2026-04-08", workerId: "w2", reagentId: "r2", completed: 95, target: 100, hours: 7.0 },
    { date: "2026-04-07", workerId: "w3", reagentId: "r1", completed: 85, target: 100, hours: 6.0 },
    { date: "2026-04-06", workerId: "w1", reagentId: "r3", completed: 100, target: 110, hours: 7.5 },
    { date: "2026-04-05", workerId: "w2", reagentId: "r1", completed: 75, target: 95, hours: 6.0 },
    { date: "2026-04-04", workerId: "w3", reagentId: "r2", completed: 88, target: 90, hours: 6.5 },
    { date: "2026-04-02", workerId: "w1", reagentId: "r2", completed: 92, target: 100, hours: 7.0 },
    { date: "2026-03-31", workerId: "w2", reagentId: "r3", completed: 70, target: 90, hours: 6.0 },
    { date: "2026-03-29", workerId: "w3", reagentId: "r1", completed: 60, target: 80, hours: 5.5 },
  ];

  const { start, end } = useMemo(() => {
    const endDate = new Date(selectedDate);
    const startDate = new Date(selectedDate);
    if (reportType === "weekly") startDate.setDate(endDate.getDate() - 6);
    if (reportType === "bimonthly") startDate.setDate(endDate.getDate() - 14);
    return {
      start: startDate.toISOString().split("T")[0],
      end: endDate.toISOString().split("T")[0],
    };
  }, [reportType, selectedDate]);

  const filteredLogs = useMemo(
    () => workLogs.filter((log) => log.date >= start && log.date <= end),
    [workLogs, start, end]
  );

  const workerPerformance = useMemo(() => {
    const list = workers.map((worker) => {
      const entries = filteredLogs.filter((l) => l.workerId === worker.id);
      const totalCompleted = entries.reduce((sum, e) => sum + e.completed, 0);
      const totalTarget = entries.reduce((sum, e) => sum + e.target, 0);
      const daysWorked = new Set(entries.map((e) => e.date)).size;
      const progressPercent = totalTarget > 0 ? Math.round((totalCompleted / totalTarget) * 100) : 0;
      return {
        ...worker,
        totalCompleted,
        totalTarget,
        daysWorked,
        avgPerDay: daysWorked > 0 ? Math.round(totalCompleted / daysWorked) : 0,
        progressPercent,
      };
    });
    return list.sort((a, b) => b.totalCompleted - a.totalCompleted);
  }, [workers, filteredLogs]);

  const reagentSummary = useMemo(() => {
    const producedByReagent = filteredLogs.reduce((acc, log) => {
      acc[log.reagentId] = (acc[log.reagentId] || 0) + log.completed;
      return acc;
    }, {});
    return reagents
      .map((r) => ({ ...r, produced: producedByReagent[r.id] || 0 }))
      .filter((r) => r.produced > 0)
      .sort((a, b) => b.produced - a.produced);
  }, [reagents, filteredLogs]);

  const totalProduced = workerPerformance.reduce((sum, w) => sum + w.totalCompleted, 0);
  const totalTarget = workerPerformance.reduce((sum, w) => sum + w.totalTarget, 0);
  const efficiency = totalTarget > 0 ? Math.round((totalProduced / totalTarget) * 100) : 0;
  const hoursWorked = Math.round(filteredLogs.reduce((sum, e) => sum + e.hours, 0) * 10) / 10;

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 text-left">
            <h2 className="m-0 text-2xl font-bold text-slate-800 text-left">Lab Reports</h2>
            <p className="mt-1 text-slate-500 text-left">Performance summaries and analytics</p>
          </div>
          <button className="flex shrink-0 items-center gap-2 self-start text-emerald-600 hover:bg-emerald-50 px-4 py-2 rounded-lg transition-colors">
            <Download size={18} />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex gap-2">
              {["daily", "weekly", "bimonthly"].map((type) => (
                <button key={type} onClick={() => setReportType(type)} className={`px-4 py-2 rounded-lg capitalize transition-colors ${reportType === type ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {type === "bimonthly" ? "Bi-Monthly" : type}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Calendar size={18} className="text-slate-500" />
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="px-4 py-2 border border-slate-200 rounded-lg" />
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-500">
            Showing data from <span className="font-medium">{start}</span> to <span className="font-medium">{end}</span>
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Package} label="Total Produced" value={String(totalProduced)} subtitle="units" color="emerald" />
          <StatCard icon={BarChart3} label="Target Achievement" value={`${efficiency}%`} subtitle={`of ${totalTarget}`} color="blue" />
          <StatCard icon={Users} label="Active Workers" value={String(workerPerformance.filter((w) => w.daysWorked > 0).length)} subtitle="people" color="amber" />
          <StatCard icon={Clock} label="Hours Worked" value={String(hoursWorked)} subtitle="total hours" color="slate" />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Users size={18} /> Worker Performance
            </h3>
          </div>
          <div className="divide-y divide-slate-100">
            {workerPerformance
              .filter((w) => w.totalCompleted > 0 || w.totalTarget > 0)
              .map((w, index) => {
              const progressPercent = w.progressPercent;
              const color = progressPercent >= 100 ? 'bg-emerald-500' : progressPercent >= 75 ? 'bg-blue-500' : progressPercent >= 50 ? 'bg-amber-500' : 'bg-red-500';
              return (
                <div key={w.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-slate-300'}`}>
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-800">{w.name}</h4>
                        <p className="text-sm text-slate-500">{w.daysWorked} days | Avg: {w.avgPerDay}/day</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-slate-800">{w.totalCompleted}</p>
                      <p className="text-sm text-slate-500">of {w.totalTarget} target</p>
                    </div>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full transition-all ${color}`} style={{ width: `${Math.min(100, progressPercent)}%` }} />
                  </div>
                  <p className="text-xs text-slate-500 text-right mt-1">{progressPercent}% achieved</p>
                </div>
              );
            })}
            {workerPerformance.every((w) => w.totalCompleted === 0 && w.totalTarget === 0) && (
              <div className="p-8 text-center text-slate-500">No work logged for this period</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Package size={18} /> Production by Reagent
            </h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {reagentSummary.map((r) => (
                <div key={r.id}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-700">{r.name}</span>
                    <span className="text-sm font-medium text-slate-800">{r.produced}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(r.produced / (reagentSummary[0]?.produced || 1)) * 100}%` }} />
                  </div>
                </div>
              ))}
              {reagentSummary.length === 0 && (
                <div className="p-4 text-center text-slate-500">No production data for this period</div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <TrendingUp size={18} /> Performance Insights
            </h3>
          </div>
          <div className="p-4 space-y-4">
            {efficiency >= 100 && (
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-emerald-800">Excellent! Team exceeded targets by {efficiency - 100}%</p>
              </div>
            )}
            {efficiency < 100 && efficiency >= 75 && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-blue-800">Good progress! Team achieved {efficiency}% of targets</p>
              </div>
            )}
            {efficiency < 75 && efficiency > 0 && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800">Team achieved {efficiency}% of targets. Consider reviewing workload distribution.</p>
              </div>
            )}
            {workerPerformance.length > 0 && workerPerformance[0].totalCompleted > 0 && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-slate-700">Top performer: <span className="font-medium">{workerPerformance[0].name}</span> with {workerPerformance[0].totalCompleted} units</p>
              </div>
            )}
            {reagentSummary.length > 0 && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-slate-700">Most produced: <span className="font-medium">{reagentSummary[0].name}</span> ({reagentSummary[0].produced} units)</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Wrapper>
  );
}



function StatCard({ icon: Icon, label, value, subtitle, color }) {
  const cls = { emerald: "bg-emerald-50 text-emerald-600", blue: "bg-blue-50 text-blue-600", amber: "bg-amber-50 text-amber-600", slate: "bg-slate-100 text-slate-600" }[color];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className={`w-10 h-10 rounded-lg ${cls} flex items-center justify-center mb-3`}><Icon size={20} /></div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
      <p className="text-xs text-slate-400">{subtitle}</p>
    </div>
  );
}
